import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Avatar,
  Slide,
  Fade,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Phone as PhoneIcon,
  Call as CallIcon,
} from '@mui/icons-material';
import api from '../api';
import ConfirmDialog from '../components/ConfirmDialog';
import SearchBar from '../components/SearchBar';
import { formatFen } from '../utils/money';

export default function WorkerList() {
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingWorker, setEditingWorker] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, id: null });
  const [searchQuery, setSearchQuery] = useState('');
  const [newWorker, setNewWorker] = useState({
    name: '',
    phone: '',
    dailyWage: '',
    smallWorkerDailyWage: '',
    overtimeHourlyWage: '',
    workType: '',
    notes: '',
  });

  useEffect(() => {
    fetchWorkers();
  }, []);

  const fetchWorkers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/api/workers');
      setWorkers(response.data);
    } catch (error) {
      console.error('Error fetching workers:', error);
      setError(error.response?.data?.error || error.message || '加载工人列表失败');
    } finally {
      setLoading(false);
    }
  };

  const submitLock = useRef(false); // 写锁：与 QuotationDetail.writeLock 同款防连点

  const handleCreateWorker = async () => {
    if (submitLock.current) return; // 防连点：请求在途时忽略重复提交
    submitLock.current = true;
    try {
      try {
        const worker = {
          id: Date.now().toString(),
          ...newWorker,
          dailyWage: Math.round(parseFloat(newWorker.dailyWage || 0) * 100),
          smallWorkerDailyWage: Math.round(parseFloat(newWorker.smallWorkerDailyWage || 0) * 100),
          overtimeHourlyWage: Math.round(parseFloat(newWorker.overtimeHourlyWage || 0) * 100),
          isActive: 1,
        };
        await api.post('/api/workers', worker);
        setOpenDialog(false);
        setNewWorker({
          name: '',
          phone: '',
          dailyWage: '',
          smallWorkerDailyWage: '',
          overtimeHourlyWage: '',
          workType: '',
          notes: '',
        });
        fetchWorkers();
      } catch (error) {
        console.error('Error creating worker:', error);
      }
    } finally {
      submitLock.current = false;
    }
  };

  const handleEditStart = (worker) => {
    setEditingWorker(worker);
    setNewWorker({
      name: worker.name || '',
      phone: worker.phone || '',
      dailyWage: (worker.dailyWage / 100).toString(),
      smallWorkerDailyWage: (worker.smallWorkerDailyWage / 100).toString(),
      overtimeHourlyWage: (worker.overtimeHourlyWage / 100).toString(),
      workType: worker.workType || '',
      notes: worker.notes || '',
    });
    setOpenDialog(true);
  };

  const handleEditSave = async () => {
    if (submitLock.current) return; // 防连点：请求在途时忽略重复提交
    submitLock.current = true;
    try {
      try {
        const worker = {
          ...newWorker,
          dailyWage: Math.round(parseFloat(newWorker.dailyWage || 0) * 100),
          smallWorkerDailyWage: Math.round(parseFloat(newWorker.smallWorkerDailyWage || 0) * 100),
          overtimeHourlyWage: Math.round(parseFloat(newWorker.overtimeHourlyWage || 0) * 100),
          isActive: editingWorker.isActive !== undefined ? editingWorker.isActive : 1,
        };
        await api.put(`/api/workers/${editingWorker.id}`, worker);
        setOpenDialog(false);
        setEditingWorker(null);
        setNewWorker({ name: '', phone: '', dailyWage: '', smallWorkerDailyWage: '', overtimeHourlyWage: '', workType: '', notes: '' });
        fetchWorkers();
      } catch (error) {
        console.error('Error updating worker:', error);
      }
    } finally {
      submitLock.current = false;
    }
  };

  const handleDelete = async (id) => {
    setConfirmDialog({ open: true, id });
  };

  const handleConfirmDelete = async () => {
    try {
      await api.delete(`/api/workers/${confirmDialog.id}`);
      setConfirmDialog({ open: false, id: null });
      fetchWorkers();
    } catch (error) {
      console.error('Error deleting worker:', error);
    }
  };


  // 搜索过滤
  const filteredWorkers = workers.filter((worker) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      worker.name?.toLowerCase().includes(query) ||
      worker.phone?.includes(query) ||
      worker.workType?.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h4" sx={{ mb: 3 }}>工人管理</Typography>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={fetchWorkers}>重新加载</Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* 页面标题和添加按钮 */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight={700}>工人管理</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
          sx={{ minHeight: 44 }}
        >
          添加工人
        </Button>
      </Box>
  
      {/* 搜索栏 */}
      <Box sx={{ mb: 3 }}>
        <SearchBar
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="搜索工人姓名、电话或工种..."
        />
      </Box>
  
      {/* 工人卡片网格 */}
      {filteredWorkers.length === 0 ? (
        <Fade in={true}>
          <Card>
            <CardContent>
              <Typography variant="h6" align="center" color="text.secondary">
                {workers.length === 0 ? '暂无工人' : '没有匹配的工人'}
              </Typography>
              <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 1 }}>
                {workers.length === 0 ? '点击“添加工人”添加第一个工人' : '尝试其他搜索条件'}
              </Typography>
            </CardContent>
          </Card>
        </Fade>
      ) : (
        <Grid container spacing={2.5}>
          {filteredWorkers.map((worker, index) => (
            <Grid item xs={12} sm={6} md={4} key={worker.id}>
              <Slide direction="up" in={true} style={{ transitionDelay: `${index * 50}ms` }}>
                <Card
                  sx={{
                    height: '100%',
                    '&:hover': {
                      borderColor: 'grey.300',
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    {/* 头像和信息头部 */}
                    <Box display="flex" alignItems="flex-start" gap={2} mb={2}>
                      <Avatar
                        sx={{
                          width: 64,
                          height: 64,
                          backgroundColor: 'primary.main',
                          fontSize: '1.5rem',
                          fontWeight: 700,
                        }}
                      >
                        {worker.name?.charAt(0)}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" fontWeight={700}>
                          {worker.name}
                        </Typography>
                        {worker.workType && (
                          <Typography variant="body2" color="primary" fontWeight={600}>
                            {worker.workType}
                          </Typography>
                        )}
                      </Box>
                      <Box>
                        <IconButton 
                          size="small" 
                          onClick={() => handleEditStart(worker)}
                          sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={() => handleDelete(worker.id)}
                          sx={{ '&:hover': { backgroundColor: 'error.lighter' } }}
                        >
                          <DeleteIcon fontSize="small" color="error" />
                        </IconButton>
                      </Box>
                    </Box>
                      
                    {/* 联系信息 */}
                    {worker.phone && (
                      <Box 
                        display="flex" 
                        alignItems="center" 
                        gap={1} 
                        mb={1.5}
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': { color: 'primary.main' },
                        }}
                        onClick={() => window.open(`tel:${worker.phone}`)}
                      >
                        <PhoneIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {worker.phone}
                        </Typography>
                        <CallIcon fontSize="small" sx={{ ml: 'auto', color: 'primary.main' }} />
                      </Box>
                    )}
                      
                    {/* 工资信息 */}
                    <Box 
                      sx={{ 
                        mt: 2, 
                        pt: 2, 
                        borderTop: '1px solid', 
                        borderColor: 'divider',
                      }}
                    >
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="body2" color="text.secondary">
                          大工日工资
                        </Typography>
                        <Typography variant="subtitle1" fontWeight={700} color="primary">
                          {formatFen(worker.dailyWage)}
                        </Typography>
                      </Box>
                        
                      {worker.smallWorkerDailyWage > 0 && (
                        <Box display="flex" justifyContent="space-between" mb={1}>
                          <Typography variant="body2" color="text.secondary">
                            小工日工资
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {formatFen(worker.smallWorkerDailyWage)}
                          </Typography>
                        </Box>
                      )}
                        
                      {worker.overtimeHourlyWage > 0 && (
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">
                            加班时薪
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {formatFen(worker.overtimeHourlyWage)}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Slide>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={openDialog} onClose={() => { setOpenDialog(false); setEditingWorker(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>{editingWorker ? '编辑工人' : '添加工人'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="工人姓名"
            fullWidth
            variant="outlined"
            value={newWorker.name}
            onChange={(e) => setNewWorker({ ...newWorker, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="电话"
            fullWidth
            variant="outlined"
            value={newWorker.phone}
            onChange={(e) => setNewWorker({ ...newWorker, phone: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="大工日工资（元）"
            fullWidth
            variant="outlined"
            type="number"
            value={newWorker.dailyWage}
            onChange={(e) => setNewWorker({ ...newWorker, dailyWage: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="小工日工资（元）"
            fullWidth
            variant="outlined"
            type="number"
            value={newWorker.smallWorkerDailyWage}
            onChange={(e) => setNewWorker({ ...newWorker, smallWorkerDailyWage: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="加班时薪（元）"
            fullWidth
            variant="outlined"
            type="number"
            value={newWorker.overtimeHourlyWage}
            onChange={(e) => setNewWorker({ ...newWorker, overtimeHourlyWage: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="备注"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={newWorker.notes}
            onChange={(e) => setNewWorker({ ...newWorker, notes: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenDialog(false); setEditingWorker(null); }}>取消</Button>
          <Button onClick={editingWorker ? handleEditSave : handleCreateWorker} variant="contained" disabled={!newWorker.name}>
            {editingWorker ? '保存' : '添加'}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, id: null })}
        onConfirm={handleConfirmDelete}
        title="确认删除"
        message="确定删除此工人？删除后不可恢复。"
      />
    </Box>
  );
}
